import { Query } from '@apollo/client/react/components';
import { assertNever } from '@tableau/data-structures';
import { DialogPositioningBehavior, DialogType, DialogWidget, TabStyles } from '@tableau/widgets';
import React from 'react';

import { Messages } from '../../../../../gen-src/Localize';
import { FieldDetailsFragment, FieldDetailsQuery, FieldDetailsQueryVariables, NonDatasourceFieldDetailsFragment, FieldDetailsDocument } from '../../../../../gen-src/queries';
import {
  divWithStyle,
  vizPortalFontStyle
} from '../../../data-catalog-shared';
import { LabelWrapper } from '../Labels/LabelsHookHelper';
import { SvgFieldIcon } from '../field/components/SvgFieldIcon';
import { iconForField } from '../field/functions/FieldFunctions';
import { FieldNameMappings } from '../field/functions/FieldNameMapping';
import { RenderQueryResult } from '../renderQueryResult';
import { columnAndFieldDetailsLabelsStyle } from '../style/ColumnAndFieldDetailsStyles';
import { FieldInlineDescriptionEditor } from './FieldInlineDescriptionEditor';
import {
  ColumnsReferencingField,
  ConnectedField,
  FieldPropertyAggregation,
  FieldPropertyAggregationParam,
  FieldPropertyBinSize,
  FieldPropertyCombinationType,
  FieldPropertyDataCategory,
  FieldPropertyDataType,
  FieldPropertyDefaultFormat,
  FieldPropertyDelimiter,
  FieldPropertyDescription,
  FieldPropertyFolderName,
  FieldPropertyFormula,
  FieldPropertyHasOther,
  FieldPropertyHasUserReference,
  FieldPropertyIsAutoGenerated,
  FieldPropertyIsHidden,
  FieldPropertyRole,
  FieldPropertySemanticRole,
  FieldsReferencedByField,
} from './FieldPropertyComponents';

const dialogWidth = 500;

export const FieldDetailsPane = divWithStyle('FieldDetailsPane', {
  ...vizPortalFontStyle,
  fontSize: TabStyles.Typography.Sizing.Default,
});

export const FieldDetailsFieldName = divWithStyle('FieldDetailsFieldName', {
  fontSize: TabStyles.Typography.Sizing.XLarge,
});

export const FieldDetailsTypeRow = divWithStyle('FieldDetailsTypeRow', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  paddingTop: TabStyles.Spacing.DefaultSpace,
});

export const FieldDetailsProperties = divWithStyle('FieldDetailsProperties', {
  display: 'table',
  paddingTop: TabStyles.Spacing.DefaultSpace,
});


export interface FieldDetailsDialogProps {
  readonly fieldId: string;
  readonly makeFieldUrl: (fieldId: string) => string;
  readonly onClose: () => void;
  readonly canEditDescription: boolean;
  readonly datasourceLuid?: string;
  readonly afterDescriptionChange?: () => void;
}

type RenderFieldDetailsDialogProps = {
  readonly fieldId: string;
  readonly makeFieldUrl: (fieldId: string) => string;
  readonly onClose: () => void;
  readonly canEditDescription: boolean;
  readonly datasourceLuid?: string;
  readonly afterDescriptionChange?: () => void;
  readonly fields: FieldDetailsQuery['fields'];
}

export function FieldDetailsDialog(props: FieldDetailsDialogProps): JSX.Element {
  return (
    <Query<FieldDetailsQuery, FieldDetailsQueryVariables>
      query={FieldDetailsDocument}
      variables={{ fieldId: props.fieldId }}
    >
      {result => <RenderQueryResult result={result} renderers={{
        renderData: data =>
          <RenderFieldDetails
            fieldId={props.fieldId}
            makeFieldUrl={props.makeFieldUrl}
            onClose={props.onClose}
            canEditDescription={props.canEditDescription}
            datasourceLuid={props.datasourceLuid}
            afterDescriptionChange={props.afterDescriptionChange}
            fields={data.fields}
          />
      }} />}
    </Query>
  );
}


function RenderFieldDetails(props: RenderFieldDetailsDialogProps): JSX.Element | null {
  const field = props.fields && props.fields[0];

  if (!field) {
    return null;
  }

  const fieldIcon = iconForField(field);

  return (
    <DialogWidget
      testId='field-details'
      title={Messages.FieldDetails()}
      type={DialogType.NormalWithTitleBar}
      positioningBehavior={DialogPositioningBehavior.Centered}
      dialogWidth={dialogWidth}
      showCloseButton={true}
      showFooter={false}
      isModal={true}
      onCloseRequest={props.onClose}
      handleGlassClick={props.onClose}
    >
      <FieldDetailsPane>
        <FieldDetailsFieldName>{field.name}</FieldDetailsFieldName>
        <FieldDetailsTypeRow>
          <SvgFieldIcon fieldIcon={fieldIcon} />
          {fieldIcon ? FieldNameMappings[fieldIcon]() : null}
        </FieldDetailsTypeRow>
        <LabelWrapper itemInfo={field} category={'all'} isEager={true} kind={'chip'} wrapper={(e) => <span style={columnAndFieldDetailsLabelsStyle}>{e}</span>} />
        <FieldDetailsProperties>
          {RenderDescriptions(field, props.canEditDescription, props.afterDescriptionChange, props.datasourceLuid)}
          <FieldPropertyFolderName folderName={field.folderName} />
          <FieldPropertyIsHidden isHidden={field.isHidden} />
          {RenderTypeSpecificFieldDetails(field, props.makeFieldUrl)}
          {RenderRelatedItems(field)}
        </FieldDetailsProperties>
      </FieldDetailsPane>
    </DialogWidget>
  );
}


function RenderDescriptions(field: FieldDetailsFragment, canEditDescription: boolean, afterDescriptionChange?: () => void, datasourceLuid?: string): JSX.Element | null {
  if (field.__typename === 'DatasourceField' && field.remoteField) {  // workbook field descriptions cannot currently be edited
    return (
      <>
        <FieldPropertyDescription labelFunc={Messages.DescriptionWorkbook} description={field.description} />
        <FieldPropertyDescription labelFunc={Messages.DescriptionDataSource} description={field.remoteField.description} />
      </>
    );
  } else {
    return (
      <FieldInlineDescriptionEditor
        canEditDescription={canEditDescription}
        description={field.description || ''}
        datasourceLuid={datasourceLuid || ''}
        fullyQualifiedFieldName={field.fullyQualifiedName || ''}
        fieldType={field.__typename}
        afterDescriptionChange={afterDescriptionChange}
      />
    );
  }
}

function RenderTypeSpecificFieldDetails(field: FieldDetailsFragment, makeFieldUrl: (fieldId: string) => string): JSX.Element | null {
  if (field.__typename !== 'DatasourceField') {
    return RenderTypeSpecificNonDatasourceFieldDetails(field, makeFieldUrl);
  } else if (field.remoteField) {
    return RenderTypeSpecificNonDatasourceFieldDetails(field.remoteField, makeFieldUrl);
  } else {
    return null;
  }
}

function RenderRelatedItems(field: FieldDetailsFragment): JSX.Element | null {
  if (field.__typename === 'ColumnField') {
    const count = field.columnsConnection ? field.columnsConnection.totalCount : null;
    return <ColumnsReferencingField fieldId={field.id} rowCount={count} />;
  } else if (field.__typename === 'DatasourceField') {
    const remoteField = field.remoteField;
    if (remoteField) {
      const datasource = remoteField.datasource;
      const datasourceVizPortalUrlId = datasource && datasource.__typename === 'PublishedDatasource' ? datasource.vizportalUrlId : null;
      const datasourceName = datasource ? datasource.name : null;
      return (
        <ConnectedField
          fieldId={remoteField.id}
          fieldName={remoteField.name}
          datasourceVizPortalUrlId={datasourceVizPortalUrlId}
          datasourceName={datasourceName}
        />
      );
    } else {
      return null;
    }
  } else {
    const count = field.fieldsConnection ? field.fieldsConnection.totalCount : null;
    return <FieldsReferencedByField fieldId={field.id} rowCount={count} />;
  }
}

export function RenderTypeSpecificNonDatasourceFieldDetails(
  field: NonDatasourceFieldDetailsFragment,
  makeFieldUrl: (fieldId: string) => string
): JSX.Element | null {
  switch (field.__typename) {
    case 'BinField':
      return (
        <>
          <FieldPropertyDataCategory dataCategory={field.dataCategory} />
          <FieldPropertyDataType dataType={field.dataType} />
          <FieldPropertyRole role={field.role} />
          <FieldPropertyFormula
            formula={field.formula}
            fields={field.fields}
            parameters={field.parameters}
            makeFieldUrl={makeFieldUrl}
          />
          <FieldPropertyBinSize binSize={field.binSize} />
        </>
      );
    case 'CalculatedField':
      return (
        <>
          <FieldPropertyDataCategory dataCategory={field.dataCategory} />
          <FieldPropertyDataType dataType={field.dataType} />
          <FieldPropertyRole role={field.role} />
          <FieldPropertyAggregation aggregation={field.aggregation} />
          <FieldPropertyAggregationParam aggregationParam={field.aggregationParam} />
          <FieldPropertyDefaultFormat defaultFormat={field.defaultFormat} />
          <FieldPropertySemanticRole semanticRole={field.semanticRole} />
          <FieldPropertyFormula
            formula={field.formula}
            fields={field.fields}
            parameters={field.parameters}
            makeFieldUrl={makeFieldUrl}
          />
          <FieldPropertyHasUserReference hasUserReference={field.hasUserReference} />
          <FieldPropertyIsAutoGenerated isAutoGenerated={field.isAutoGenerated} />
        </>
      );
    case 'ColumnField':
      return (
        <>
          <FieldPropertyDataCategory dataCategory={field.dataCategory} />
          <FieldPropertyDataType dataType={field.dataType} />
          <FieldPropertyRole role={field.role} />
          <FieldPropertyAggregation aggregation={field.aggregation} />
          <FieldPropertyAggregationParam aggregationParam={field.aggregationParam} />
          <FieldPropertyDefaultFormat defaultFormat={field.defaultFormat} />
          <FieldPropertySemanticRole semanticRole={field.semanticRole} />
        </>
      );
    case 'CombinedField':
      return null;
    case 'CombinedSetField':
      return (
        <>
          <FieldPropertyCombinationType combinationType={field.combinationType} />
          <FieldPropertyDelimiter delimiter={field.delimiter} />
        </>
      );
    // We don't expect datasource fields here;
    // they're handled in renderTypeSpecificFieldDetails.
    case 'DatasourceField':
      return null;
    case 'GroupField':
      return (
        <>
          <FieldPropertyDataCategory dataCategory={field.dataCategory} />
          <FieldPropertyDataType dataType={field.dataType} />
          <FieldPropertyRole role={field.role} />
          <FieldPropertyHasOther hasOther={field.hasOther} />
        </>
      );
    case 'HierarchyField':
      return null;
    case 'SetField':
      return null;
    default:
      assertNever(field);
      return null;
  }
}
